import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import { toast } from 'react-toastify';

const TYPE_META = {
  material: { label: 'Material Master', icon: '◈', gradient: 'linear-gradient(135deg,#D95A1A,#e87a3a)', route: '/user/request/material' },
  vendor:   { label: 'Vendor Master',   icon: '◉', gradient: 'linear-gradient(135deg,#1A2C80,#2d3fa0)', route: '/user/request/vendor' },
};

const DraftRequests = () => {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = () =>
    api.get('/drafts').then(r => setDrafts(r.data)).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const handleRestore = (draft) => {
    navigate(TYPE_META[draft.type].route);
    toast.info(`Opening ${TYPE_META[draft.type].label} draft…`, { autoClose: 1500 });
  };

  const handleDelete = async (draft) => {
    try {
      await api.delete(`/drafts/${draft.type}`);
      setDrafts(prev => prev.filter(d => d.id !== draft.id));
      toast.success('Draft deleted');
    } catch { toast.error('Failed to delete draft'); }
  };

  const fieldCount = (draft) => {
    try { return Object.values(draft.data || {}).filter(v => String(v ?? '').trim()).length; }
    catch { return 0; }
  };

  return (
    <div>
      {/* Header */}
      <div style={S.banner}>
        <div style={S.bannerCircle} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={S.bannerTitle}>Saved Drafts</div>
          <div style={S.bannerSub}>Resume a draft request where you left off</div>
        </div>
      </div>

      <div style={S.card}>
        <div style={S.cardTop}>
          <div style={S.cardTitle}>Your Drafts</div>
          <div style={S.badge}>{drafts.length} saved</div>
        </div>

        {loading ? (
          <div style={S.empty}>Loading…</div>
        ) : drafts.length === 0 ? (
          <div style={S.empty}>No drafts saved yet. Use "💾 Save Draft" while filling a request form.</div>
        ) : (
          <div style={S.list}>
            {drafts.map(draft => {
              const meta = TYPE_META[draft.type] || TYPE_META.material;
              const fc = fieldCount(draft);
              const approverCount = (draft.approvers || []).filter(a => a.name && a.email).length;
              return (
                <div key={draft.id} style={S.row}>
                  <div style={{ ...S.iconBox, background: meta.gradient }}>
                    {meta.icon}
                  </div>
                  <div style={S.info}>
                    <div style={S.draftTitle}>{draft.title || <span style={S.untitled}>Untitled</span>}</div>
                    <div style={S.draftMeta}>
                      <span style={S.typeBadge}>{meta.label}</span>
                      {fc > 0 && <span style={S.pill}>{fc} field{fc !== 1 ? 's' : ''} filled</span>}
                      {approverCount > 0 && <span style={S.pill}>{approverCount} approver{approverCount !== 1 ? 's' : ''}</span>}
                      {draft.description && <span style={S.descSnippet}>{String(draft.description).slice(0, 60)}{draft.description.length > 60 ? '…' : ''}</span>}
                    </div>
                    <div style={S.draftDate}>Last saved: {new Date(draft.updated_at).toLocaleString()}</div>
                  </div>
                  <div style={S.actions}>
                    <button style={S.btnRestore} onClick={() => handleRestore(draft)}>
                      ↩ Resume
                    </button>
                    <button style={S.btnDelete} onClick={() => handleDelete(draft)}>
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const S = {
  banner: {
    background: 'linear-gradient(135deg,#374151,#4b5563)',
    borderRadius: 14, padding: '24px 28px', marginBottom: 20,
    position: 'relative', overflow: 'hidden',
  },
  bannerCircle: {
    position: 'absolute', width: 200, height: 200, borderRadius: '50%',
    background: 'rgba(255,255,255,0.06)', top: -60, right: -40,
  },
  bannerTitle: { color: '#fff', fontSize: 20, fontWeight: 800, marginBottom: 4 },
  bannerSub: { color: 'rgba(255,255,255,0.7)', fontSize: 13 },
  card: {
    background: '#fff', borderRadius: 14,
    boxShadow: '0 2px 12px rgba(11,21,84,0.06)', overflow: 'hidden',
  },
  cardTop: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '18px 24px', borderBottom: '1px solid #f1f5f9',
  },
  cardTitle: { fontSize: 14, fontWeight: 800, color: '#0B1554' },
  badge: { background: '#f0f4ff', color: '#4f6ef7', fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20 },
  empty: { padding: '40px 24px', textAlign: 'center', color: '#94a3b8', fontSize: 14 },
  list: { display: 'flex', flexDirection: 'column' },
  row: {
    display: 'flex', alignItems: 'center', gap: 16,
    padding: '16px 24px', borderBottom: '1px solid #f1f5f9',
  },
  iconBox: {
    width: 44, height: 44, borderRadius: 12, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 20, color: '#fff',
  },
  info: { flex: 1, minWidth: 0 },
  draftTitle: { fontSize: 14, fontWeight: 700, color: '#0B1554', marginBottom: 4 },
  untitled: { color: '#94a3b8', fontStyle: 'italic' },
  draftMeta: { display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 4 },
  typeBadge: { background: '#f0f4ff', color: '#4f6ef7', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6 },
  pill: { background: '#f1f5f9', color: '#64748b', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6 },
  descSnippet: { fontSize: 12, color: '#94a3b8' },
  draftDate: { fontSize: 11.5, color: '#94a3b8' },
  actions: { display: 'flex', gap: 8, flexShrink: 0 },
  btnRestore: {
    padding: '8px 18px', background: 'linear-gradient(135deg,#0d1a6e,#4f6ef7)',
    color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
  },
  btnDelete: {
    padding: '8px 14px', background: '#fff5f5', color: '#DD3222',
    border: '1.5px solid #fecaca', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
};

export default DraftRequests;
